import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const OnboardingModal = ({ isOpen, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    currentAge: '',
    retirementAge: '',
    projectionHorizon: ''
  });

  const steps = [
    {
      title: "What's your current age?",
      subtitle: "This helps us calculate your retirement timeline",
      field: 'currentAge',
      placeholder: 'e.g., 35',
      type: 'number'
    },
    {
      title: "When do you plan to retire?",
      subtitle: "Your target retirement age",
      field: 'retirementAge',
      placeholder: 'e.g., 65',
      type: 'number'
    },
    {
      title: "How far ahead should we project?",
      subtitle: "Years beyond retirement to plan for",
      field: 'projectionHorizon',
      placeholder: 'e.g., 30',
      type: 'number'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(formData);
  };

  const handleSkip = () => {
    onSkip();
  };

  const currentStepData = steps[currentStep - 1];
  const isLastStep = currentStep === steps.length;
  const canProceed = formData[currentStepData.field] && formData[currentStepData.field].trim() !== '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-card rounded-xl border border-border shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Welcome to RetireProApp</h2>
              <p className="text-sm text-muted-foreground">Let's set up your retirement planning</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Skip onboarding"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3">
          <div className="flex items-center gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  index < currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Step {currentStep} of {steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {currentStepData.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {currentStepData.subtitle}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <input
                type={currentStepData.type}
                value={formData[currentStepData.field]}
                onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                placeholder={currentStepData.placeholder}
                className="w-full px-4 py-3 text-lg text-center border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>

            {/* Smart defaults suggestions */}
            {currentStep === 1 && (
              <div className="flex gap-2 justify-center">
                {[25, 30, 35, 40, 45].map(age => (
                  <button
                    key={age}
                    onClick={() => handleInputChange('currentAge', age.toString())}
                    className="px-3 py-1 text-sm bg-muted hover:bg-primary hover:text-primary-foreground rounded-md transition-colors"
                  >
                    {age}
                  </button>
                ))}
              </div>
            )}

            {currentStep === 2 && (
              <div className="flex gap-2 justify-center">
                {[60, 65, 70].map(age => (
                  <button
                    key={age}
                    onClick={() => handleInputChange('retirementAge', age.toString())}
                    className="px-3 py-1 text-sm bg-muted hover:bg-primary hover:text-primary-foreground rounded-md transition-colors"
                  >
                    {age}
                  </button>
                ))}
              </div>
            )}

            {currentStep === 3 && (
              <div className="flex gap-2 justify-center">
                {[25, 30, 35].map(years => (
                  <button
                    key={years}
                    onClick={() => handleInputChange('projectionHorizon', years.toString())}
                    className="px-3 py-1 text-sm bg-muted hover:bg-primary hover:text-primary-foreground rounded-md transition-colors"
                  >
                    {years}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Back
            </button>
            
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-muted-foreground hover:text-foreground"
              >
                Skip for now
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                className="min-w-[100px]"
              >
                {isLastStep ? 'Complete' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
